import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '../Profile/Avatar';
import { Username } from '../Profile/Username';
import { formatCategoryName } from '../../lib/utils/formatters';
import type { Venue } from '../../types/venues';

interface VenueCardProps {
  venue: Venue;
  showStatus?: boolean;
  distance?: number;
  isInactive?: boolean;
}

export function VenueCard({ venue, showStatus = false, distance, isInactive = false }: VenueCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/venues/${venue.slug}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="transition-all duration-150 cursor-pointer active:scale-[0.98]"
    >
      <div className="aspect-w-16 aspect-h-9 relative rounded-2xl overflow-hidden">
        <img
          src={venue.images?.[0] || 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?auto=format&fit=crop&q=80&w=400'}
          alt={venue.name}
          className="w-full h-48 object-cover"
          loading="lazy"
        />
      </div>
      
      <div className="pt-4">
        <div className="flex items-center justify-between mb-4">
          {venue.user && (
            <Link 
              to={`/profile/${venue.user_id}/listings`}
              className="flex items-center space-x-2 group flex-1 min-w-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar 
                url={venue.user.avatar_url} 
                size="sm" 
                userId={venue.user_id}
                editable={false}
              />
              <div className="min-w-0">
                <Username 
                  username={venue.user.username || 'Anonymous'}
                  userId={venue.user_id}
                  className="text-sm text-content group-hover:text-accent-text truncate"
                />
                <p className="text-xs text-content/60 mt-0.5">Venue Owner</p>
              </div>
            </Link>
          )}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <span className="px-2 py-1 text-xs font-medium bg-accent-base text-accent-text rounded-full">
              {venue.venue_type ? formatCategoryName(venue.venue_type) : t('venues.upToPeople', { count: venue.capacity })}
            </span>
            {isInactive && (
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                Inactive
              </span>
            )}
            {showStatus && venue.approval_status === 'pending' && (
              <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                {t('common.pending')}
              </span>
            )}
          </div>
        </div>

        <h3 className="text-lg font-semibold text-content mb-2">
          {venue.name}
        </h3>

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-sm text-content/80">
            <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/location-icon.png" alt="Location" className="h-4 w-4 mr-2" />
            <span>{venue.address}</span>
            {distance !== undefined && (
              <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
                {distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`}
              </span>
            )}
          </div>
          <div className="flex items-center text-sm text-content/80">
            <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/users-capacity.png" alt="Capacity" className="h-4 w-4 mr-2" />
            <span>{t('venues.upToPeople', { count: venue.capacity })}</span>
          </div>
          {venue.available_for_rent && (
            <div className="flex items-center text-sm text-content/80">
              <img src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/icons/Key-rent.png" alt="Rent" className="h-4 w-4 mr-2" />
              <span className="font-medium text-accent-text">{t('venues.availableForRent')}</span>
              {venue.price && (
                <span className="ml-1.5 text-content/60">
                  — {getCurrencySymbol(venue.currency)}{venue.price}/{venue.price_period || 'hour'}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {venue.amenities.slice(0, 3).map((amenity, index) => (
            <span
              key={index}
              className="px-2 py-1 text-xs bg-accent-base text-accent-text rounded-full"
            >
              {amenity.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')}
            </span>
          ))}
          {venue.amenities.length > 3 && (
            <span className="px-2 py-1 text-xs bg-accent-base text-accent-text rounded-full">
              +{venue.amenities.length - 3} {t('common.more')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper function to convert currency code to symbol
function getCurrencySymbol(currency: string = 'EUR'): string {
  const symbols: Record<string, string> = {
    'EUR': '€',
    'USD': '$',
    'GBP': '£',
    'CHF': 'CHF'
  };
  
  return symbols[currency] || currency;
}
